import React, { useRef } from 'react';
import { MenuItem } from '@mui/material';
import { Button } from '@/components/ui/button';
import { AppOutlinedTextField } from '@/components/shared/AppOutlinedTextField';
import { Check, Lock, UsersRound } from 'lucide-react';
import { COOKING_METHOD_OPTIONS, DISH_TYPE_OPTIONS, MEAL_TYPE_OPTIONS } from '@/constants/dietary-goals';

const numberInputProps = {
  inputProps: {
    min: 0,
    onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
    style: { MozAppearance: 'textfield' as const },
  },
  sx: {
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '& input[type=number]': {
      MozAppearance: 'textfield',
    },
  },
};

interface RecipeInformationFormProps {
  recipeName: string;
  aliases: string;
  description: string;
  servings: number | '';
  dishTypes: string[];
  mealTypes: string[];
  cookingMethod: string;
  prepTimeMinutes: number | '';
  cookTimeMinutes: number | '';
  totalTimeMinutes: number;
  shareWithOthers: boolean;
  recipeImagePreviewUrl: string | null;
  onRecipeNameChange: (value: string) => void;
  onAliasesChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onServingsChange: (value: number | '') => void;
  onDishTypesChange: (value: string[]) => void;
  onMealTypesChange: (value: string[]) => void;
  onCookingMethodChange: (value: string) => void;
  onPrepTimeChange: (value: number | '') => void;
  onCookTimeChange: (value: number | '') => void;
  onShareWithOthersChange: (value: boolean) => void;
  onRecipeImageFileSelect: (file: File | null) => void;
  /** When set (e.g. edit recipe), nutrition servings is editable instead of fixed at 1. */
  nutritionServings?: number;
  onNutritionServingsChange?: (value: number) => void;
  /** Hide image upload (e.g. when update API does not accept new image yet). Default true. */
  showRecipeImageUpload?: boolean;
  /** `edit` never shows the image upload block (create flow only). */
  variant?: 'create' | 'edit';
}

export const RecipeInformationForm: React.FC<RecipeInformationFormProps> = ({
  recipeName,
  aliases,
  description,
  servings,
  dishTypes,
  mealTypes,
  cookingMethod,
  prepTimeMinutes,
  cookTimeMinutes,
  totalTimeMinutes,
  shareWithOthers,
  recipeImagePreviewUrl,
  onRecipeNameChange,
  onAliasesChange,
  onDescriptionChange,
  onServingsChange,
  onDishTypesChange,
  onMealTypesChange,
  onCookingMethodChange,
  onPrepTimeChange,
  onCookTimeChange,
  onShareWithOthersChange,
  onRecipeImageFileSelect,
  nutritionServings: nutritionServingsProp,
  onNutritionServingsChange,
  showRecipeImageUpload = true,
  variant = 'create',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Create: respect showRecipeImageUpload. Edit: always show so existing URLs display and missing images can be added. */
  const showRecipeImageSection =
    (variant === 'create' && showRecipeImageUpload) || variant === 'edit';

  const dishValue = dishTypes[0] || '';
  const cookingValue = cookingMethod || '';

  const labelClass = 'text-sm font-medium text-slate-900';

  return (
    <div className="space-y-6" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <AppOutlinedTextField
            id="recipe-name"
            label="Recipe Name"
            value={recipeName}
            onChange={(e) => onRecipeNameChange(e.target.value)}
            placeholder="Dal tadka"
            fullWidth
            size="small"
          />
        </div>

        <div className="md:col-span-2">
          <AppOutlinedTextField
            id="recipe-aliases"
            label="Aliases"
            value={aliases}
            onChange={(e) => onAliasesChange(e.target.value)}
            placeholder="Alternative names, separated by commas"
            fullWidth
            size="small"
          />
        </div>

        <div className="md:col-span-2">
          <AppOutlinedTextField
            id="description"
            label="Description"
            required
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="Brief Description of the recipe"
            fullWidth
            multiline
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                alignItems: 'flex-start',
                paddingY: '4px',
              },
            }}
          />
        </div>

        <div>
          <AppOutlinedTextField
            id="servings"
            label="Servings"
            required
            type="number"
            value={servings}
            onChange={(e) => {
              const rawValue = e.target.value;
              if (rawValue === '') {
                onServingsChange('');
                return;
              }

              const parsedValue = Number.parseInt(rawValue, 10);
              if (!Number.isNaN(parsedValue)) {
                onServingsChange(parsedValue);
              }
            }}
            onBlur={(e) => {
              const rawValue = e.target.value.trim();
              if (rawValue === '') return;

              const parsedValue = Number.parseInt(rawValue, 10);
              if (Number.isNaN(parsedValue) || parsedValue < 1) {
                onServingsChange(1);
              }
            }}
            fullWidth
            size="small"
            inputProps={{ min: 1, onWheel: (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur() }}
            sx={numberInputProps.sx}
          />
        </div>

        <div>
          <AppOutlinedTextField
            id="nutrition-servings"
            label="Nutrition Servings"
            required
            type="number"
            value={1}
            disabled
            fullWidth
            size="small"
          />
          <p
            className="mt-2 text-[#2EB4E4]"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: '0.75rem',
              lineHeight: '12px',
              letterSpacing: '0.15px',
              verticalAlign: 'middle',
              leadingTrim: 'none',
            } as React.CSSProperties}
          >
            Fixed at 1 for custom recipes
          </p>
        </div>

        <div>
          <AppOutlinedTextField
            select
            id="dish-type"
            label="Dish Type"
            value={dishValue}
            onChange={(e) => {
              const val = e.target.value as string;
              onDishTypesChange(val ? [val] : []);
            }}
            fullWidth
            size="small"
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" sx={{ fontSize: '14px', color: '#94a3b8' }}>
              Not specified
            </MenuItem>
            {DISH_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                {option.name}
              </MenuItem>
            ))}
          </AppOutlinedTextField>
        </div>

        <div>
          <AppOutlinedTextField
            select
            id="meal-type"
            label="Meal Type"
            required
            value={mealTypes || []}
            onChange={(e) => {
              const val = e.target.value;
              onMealTypesChange(typeof val === 'string' ? val.split(',') : (val as string[]));
            }}
            fullWidth
            size="small"
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: (selected) => {
                const values = selected as string[];
                if (!values || values.length === 0) {
                  return <span style={{ color: '#94a3b8' }}>Not specified</span>;
                }
                const labels = MEAL_TYPE_OPTIONS
                  .filter((option) => values.includes(option.value))
                  .map((option) => option.name);
                // Fallback for custom values not in MEAL_TYPE_OPTIONS
                const unknownValues = values.filter((v) => !MEAL_TYPE_OPTIONS.some((o) => o.value === v));
                return <span style={{ color: '#0f172a' }}>{[...labels, ...unknownValues].join(', ')}</span>;
              },
            }}
          >
            {MEAL_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                {option.name}
              </MenuItem>
            ))}
          </AppOutlinedTextField>
        </div>

        <div>
          <AppOutlinedTextField
            select
            id="cooking-method"
            label="Cooking Method"
            required
            value={cookingValue}
            onChange={(e) => onCookingMethodChange((e.target.value as string) || '')}
            fullWidth
            size="small"
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" sx={{ fontSize: '14px', color: '#94a3b8' }}>
              Not specified
            </MenuItem>
            {COOKING_METHOD_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '14px' }}>
                {option.name}
              </MenuItem>
            ))}
          </AppOutlinedTextField>
        </div>

        <div>
          <AppOutlinedTextField
            id="prep-time"
            label="Prep Time (minutes)"
            type="number"
            value={prepTimeMinutes === '' || prepTimeMinutes === 0 ? '' : prepTimeMinutes}
            placeholder="15"
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                onPrepTimeChange('');
              } else {
                const prepTime = parseInt(value, 10);
                if (!Number.isNaN(prepTime) && prepTime >= 0) {
                  onPrepTimeChange(prepTime);
                }
              }
            }}
            onBlur={(e) => {
              if (e.target.value === '' || e.target.value === '0') {
                onPrepTimeChange(0);
              }
            }}
            fullWidth
            size="small"
            inputProps={numberInputProps.inputProps}
            sx={numberInputProps.sx}
          />
        </div>

        <div>
          <AppOutlinedTextField
            id="cook-time"
            label="Cook Time (minutes)"
            type="number"
            value={cookTimeMinutes === '' || cookTimeMinutes === 0 ? '' : cookTimeMinutes}
            placeholder="30"
            onChange={(e) => {
              const value = e.target.value;
              if (value === '') {
                onCookTimeChange('');
              } else {
                const cookTime = parseInt(value, 10);
                if (!Number.isNaN(cookTime) && cookTime >= 0) {
                  onCookTimeChange(cookTime);
                }
              }
            }}
            onBlur={(e) => {
              if (e.target.value === '' || e.target.value === '0') {
                onCookTimeChange(0);
              }
            }}
            fullWidth
            size="small"
            inputProps={numberInputProps.inputProps}
            sx={numberInputProps.sx}
          />
        </div>

        <div>
          <AppOutlinedTextField
            id="total-time"
            label="Total Time"
            type="number"
            value={totalTimeMinutes}
            disabled
            fullWidth
            size="small"
          />
        </div>

      </div>

      <div>
        <p className={`${labelClass} mb-3`}>Visibility</p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => onShareWithOthersChange(false)}
            className={`relative inline-flex min-w-[220px] flex-col items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors ${
              !shareWithOthers
                ? 'border-[#2EB4E4] bg-[#F0FAFD]'
                : 'border-[#E5E7EB] bg-white hover:border-slate-300'
            }`}
          >
            {!shareWithOthers && (
              <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#2EB4E4] text-white">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}
            <Lock className={`h-6 w-6 ${!shareWithOthers ? 'text-[#2EB4E4]' : 'text-slate-400'}`} />
            <div>
              <p className="text-sm font-semibold text-slate-900">Private</p>
              <p className="mt-0.5 text-xs text-slate-500">Only visible to you</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => onShareWithOthersChange(true)}
            className={`relative inline-flex min-w-[220px] flex-col items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors ${
              shareWithOthers
                ? 'border-[#2EB4E4] bg-[#F0FAFD]'
                : 'border-[#E5E7EB] bg-white hover:border-slate-300'
            }`}
          >
            {shareWithOthers && (
              <span className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-[#2EB4E4] text-white">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
            )}
            <UsersRound className={`h-6 w-6 ${shareWithOthers ? 'text-[#2EB4E4]' : 'text-slate-400'}`} />
            <div>
              <p className="text-sm font-semibold text-slate-900">Shared with Practice</p>
              <p className="mt-0.5 text-xs text-slate-500">Other practitioners can use this</p>
            </div>
          </button>
        </div>
      </div>

      {showRecipeImageSection ? (
        <div>
          <p className={`${labelClass} mb-3`}>
            {variant === 'edit' ? 'Recipe image' : 'Upload Recipe Image'}
          </p>
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#CBD5E1] bg-slate-50/50 px-4 py-10">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                onRecipeImageFileSelect(file);
                e.target.value = '';
              }}
            />
            <p className="text-center text-sm text-slate-500">Upload JPEG, PNG, or WebP (max 5MB)</p>
            {recipeImagePreviewUrl && (
              <img
                src={recipeImagePreviewUrl}
                alt="Recipe preview"
                className="mt-4 max-h-40 max-w-full rounded-lg object-contain"
              />
            )}
            <Button
              type="button"
              variant="outline"
              className="mt-4 h-10 rounded-lg border-[#2EB4E4] bg-white px-6 text-xs font-semibold tracking-wide text-[#2EB4E4] hover:bg-[#F0FAFD]"
              onClick={() => fileInputRef.current?.click()}
            >
              CHOOSE FILE
            </Button>
            {recipeImagePreviewUrl && (
              <Button
                type="button"
                variant="ghost"
                className="mt-2 text-xs text-slate-500"
                onClick={() => onRecipeImageFileSelect(null)}
              >
                Remove image
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
